import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import './infohint.css';

/**
 * Kleines Informationssymbol neben einer Beschriftung mit einem Erklärtext in einfachem Deutsch. Am Desktop erscheint der Text beim Darüberfahren und bei
 * Tastaturfokus, auf dem Handy per Tipp (Tippen daneben oder Escape schließt ihn). Der Text hängt per `aria-describedby` am Knopf, damit Screenreader ihn vorlesen.
 */
export function InfoHint({ label, children, className = '' }: { label: string; children: ReactNode; className?: string }) {
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState<{ left: number; top: number | 'auto'; bottom: number | 'auto'; maxHeight?: number }>({ left: 8, top: 8, bottom: 'auto' });
  const id = useId();
  const root = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const text = useRef<HTMLSpanElement>(null);

  /**
   * Lage des Textes im Fenster: unter dem Symbol, wenn dort Platz für den ganzen Text ist, sonst darüber (oder dort, wo mehr Platz ist), seitlich ins Fenster geschoben.
   * Ist auch dort zu wenig Platz, wird der Text so hoch wie der Platz und lässt sich scrollen.
   */
  const position = () => {
    const rect = button.current?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.min(352, window.innerWidth * 0.8);
    const left = Math.max(8, Math.min(rect.left + rect.width / 2 - width / 2, window.innerWidth - width - 8));
    const below = window.innerHeight - rect.bottom - 14;
    const above = rect.top - 14;
    const need = text.current?.scrollHeight ?? 0;
    setPlace(
      need <= below || below >= above
        ? { left, top: rect.bottom + 6, bottom: 'auto', maxHeight: Math.max(120, below) }
        : { left, top: 'auto', bottom: window.innerHeight - rect.top + 6, maxHeight: Math.max(120, above) },
    );
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const onPointer = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  return (
    <span className={`hint${open ? ' is-open' : ''}${className ? ` ${className}` : ''}`} ref={root} onMouseEnter={position} onFocus={position}>
      <button type="button" ref={button} className="hint__button" aria-label={`Hinweis zu ${label}`} aria-describedby={id} aria-expanded={open} onClick={() => {
          position();
          setOpen((o) => !o);
        }}>
        i
      </button>
      <span role="tooltip" id={id} className="hint__text" ref={text} style={place}>{children}</span>
    </span>
  );
}
